import store from '@/store'
import { httpHelper } from '@/utils'

export const commonService = {
    populateData,
    populateUsers,
}

function hasUserManagementFetchAccess() {
    return (
        store.getters['auth/isAdminOrStaff'] ||
        store.getters['auth/isAccountAdmin'] ||
        store.getters['auth/hasUserManagementAccess']
    )
}

function canFetchUsers() {
    return hasUserManagementFetchAccess()
}

function canFetchRoles() {
    return hasUserManagementFetchAccess()
}

function canFetchSimulatableUsers() {
    return (
        store.getters['auth/isAdminOrStaff'] ||
        store.getters['auth/isOriginalStaff']
    )
}

function populateData() {
    const isSimulationMode = store.getters['auth/isSimulationMode']
    const shouldFetchUsers = canFetchUsers()
    const shouldFetchRoles = canFetchRoles()
    const shouldFetchSimulatableUsers = canFetchSimulatableUsers()

    const requests = [
        fetchAssetTypes().then(value => {
            store.state.tracker.assetTypes = value
        }),
        fetchLocations().then(value => {
            store.state.location.locations = value
        }),
        fetchTrackers().then(value => {
            store.state.tracker.trackers = value
        }),
    ]

    store.dispatch('preferences/fetchPreferences')

    if (store.getters['auth/hasLsvaAccess']) {
        requests.push(store.dispatch('lsva/fetchAllTrucks'))
    }

    if (shouldFetchUsers) {
        requests.push(
            fetchUsers({
                useOriginalToken: false,
            }).then(value => {
                store.state.user.users = value
            })
        )
    } else {
        store.state.user.users = []
    }

    if (shouldFetchRoles) {
        requests.push(
            fetchRoles({
                useOriginalToken: false,
            }).then(value => {
                store.state.role.roles = value
            })
        )
    } else {
        store.state.role.roles = []
    }

    if (shouldFetchSimulatableUsers) {
        requests.push(
            fetchUsers({
                useOriginalToken: isSimulationMode,
            }).then(value => {
                store.state.user.simulatableUsers = value
            })
        )
    } else {
        store.state.user.simulatableUsers = []
    }

    if (store.getters['auth/hasMaintenanceAccess']) {
        requests.push(
            fetchMaintenancePolicies().then(value => {
                store.state.maintenance.maintenancePolicies = value
            })
        )
    } else {
        store.state.maintenance.maintenancePolicies = []
    }

    if (store.getters['auth/isAxRoad']) {
        if (store.getters['auth/hasTachographAccess']) {
            requests.push(
                fetchDrivers().then(value => {
                    store.state.tracker.drivers = value
                })
            )
        }
        // TODO: maybe use another flag for warnings
        if (store.getters['auth/hasRulesAccess']) {
            requests.push(
                fetchWarnings().then(value => {
                    store.state.warnings.allWarnings = value
                    store.state.warnings.warnings = value.filter(
                        warning => !warning.resolved
                    )
                })
            )
        }
    }

    return Promise.all(requests)
}

async function populateUsers() {
    if (canFetchSimulatableUsers()) {
        const useOriginalToken = store.getters['auth/isSimulationMode']
        store.state.user.simulatableUsers = await fetchUsers({
            useOriginalToken,
        })
    }
}

async function fetchAssetTypes() {
    const { data } = await httpHelper.get('/assettypes/')
    return data.results
}

async function fetchLocations() {
    const { data } = await httpHelper.getRecursively('/locations/', {
        params: {
            limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        },
    })
    return data.results
}

async function fetchMaintenancePolicies() {
    const { data } = await httpHelper.getRecursively('/maintenance-policies/', {
        params: {
            limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        },
    })
    return data.results
}

async function fetchTrackers() {
    const { data } = await httpHelper.getRecursively('/trackers/', {
        params: {
            limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        },
    })
    return data.results
}

async function fetchUsers({ useOriginalToken = false } = {}) {
    const originalConfig = useOriginalToken
        ? await httpHelper.getOriginalConfig()
        : undefined

    if (useOriginalToken && !originalConfig) {
        return []
    }

    const { data } = await httpHelper.getRecursively('/users/', {
        ...originalConfig,
        params: {
            limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        },
    })
    return data.results
}

async function fetchRoles({ useOriginalToken = false } = {}) {
    const originalConfig = useOriginalToken
        ? await httpHelper.getOriginalConfig()
        : undefined
    if (useOriginalToken && !originalConfig) {
        return []
    }

    const { data } = await httpHelper.getRecursively('/roles/', {
        ...originalConfig,
        params: {
            limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        },
    })
    return data.results
}

async function fetchDrivers() {
    const { data } = await httpHelper.getRecursively('/tachograph/drivers/', {
        params: {
            limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        },
    })
    return data.results
}

async function fetchWarnings() {
    const { data } = await httpHelper.getRecursively(
        '/alertrule-notifications/',
        {
            params: {
                limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
            },
        }
    )
    return data.results
}
